import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, takeEvery } from 'redux-saga/effects'

import api from '@/api/_legacy'
import {
  AddNote,
  ApiBusinessUnit,
  ApiClient,
  ApiDividendProcessing,
  ApiNote
} from '@/api/_legacy/generated-api'
import { createFetchSaga, createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../../constants'
import {
  extractErrorMessagesFromServerResponse,
  formatFees,
  getDateOnlyAsJson
} from '../../../util'
import { getUtcDateAsString } from '../../../util/get-utc-date'
import { Errors, validator } from '../../../util/validator'
import { MaturityDateType, Note } from '../types'
import { newNoteRules, NewNoteState } from './validation-rules'

const initialState: NewNoteState = {
  canProceed: false,
  note: {
    client: undefined,
    businessUnit: undefined,
    isExternal: false,
    noteId: '',
    noteName: '',
    startDate: new Date().toString(),
    endDate: new Date().toString(),
    dividendProcessing: 'Reinvested',
    // TODO: taxClassification is not needed any more
    taxClassification: 'Capital',
    businessUnitId: '',
    counterPartyId: '',
    isCollateralForLoan: false
  },
  noteNames: [],
  tenant: '',
  clients: [],
  isCollateralSet: false,
  currentBusinessUnits: [],
  counterpartyIds: [],
  maturityDateType: undefined,
  errors: {}
}

const clearError = (fieldName: keyof Note, state: NewNoteState, test: any) => {
  if (test) {
    state.errors[fieldName as string] = undefined
  }
}

export const newNoteSlice = createSlice({
  name: 'newNote',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<boolean>) => {
      state.isCollateralSet = action.payload
    },
    fetchClientsSuccess: (
      state,
      action: PayloadAction<{ clients: ApiClient[]; userType: string }>
    ) => {
      state.clients = action.payload.clients
      if (action.payload.userType === 'Editor') {
        state.note.client = action.payload.clients[0]
        state.note.businessUnit = action.payload.clients[0].businessUnits[0]
        state.note.businessUnitId = state.note.businessUnit.businessUnitId
      }
    },
    fetchNotesSuccess: (state, action: PayloadAction<ApiNote[]>) => {
      state.noteNames = action.payload.map(x => x.noteName)
    },
    fetchCounterpartyIdsSuccess: (state, action: PayloadAction<string[]>) => {
      state.counterpartyIds = action.payload
    },
    setClient: (state, action: PayloadAction<string>) => {
      state.note.client = state.clients.find(x => x.clientId === action.payload)

      state.currentBusinessUnits = state.note.client?.businessUnits

      clearError('client', state, state.note.client)

      if (state.note.client) {
        state.fees = formatFees(state.note?.client?.defaultFees?.defaultNoteFees)
      } else {
        state.fees = undefined
      }
    },
    setBusinessUnit: (state, action: PayloadAction<string>) => {
      state.note.businessUnit = state.currentBusinessUnits.find(
        x => x.businessUnitId === action.payload
      )
      state.note.businessUnitId = action.payload

      clearError('businessUnit', state, state.note.businessUnitId)
    },
    setNoteName: (state, action: PayloadAction<string>) => {
      state.note.noteName = action.payload
      state.errors.noteName = undefined
    },
    setStartDate: (state, action: PayloadAction<string>) => {
      if (action.payload) {
        state.note.startDate = getUtcDateAsString(new Date(action.payload))
      } else {
        state.note.startDate = ''
      }

      clearError('startDate', state, state.note.startDate)
    },
    setMaturityDateType: (state, action: PayloadAction<MaturityDateType>) => {
      state.maturityDateType = action.payload
      if (action.payload === 'perpetual') {
        state.note.endDate = ''
      } else {
        state.note.endDate = getUtcDateAsString(new Date())
      }

      clearError('endDate', state, state.maturityDateType)
    },
    setCollateral: (state, action: PayloadAction<boolean>) => {
      state.isCollateralSet = true
      state.note.isCollateralForLoan = action.payload
      clearError('isCollateralForLoan', state, state.note.isCollateralForLoan)
    },
    setMaturityDate: (state, action: PayloadAction<string>) => {
      if (action.payload) {
        state.note.endDate = getUtcDateAsString(new Date(action.payload))
      } else {
        state.note.endDate = ''
      }

      clearError('endDate', state, state.note.endDate)
    },
    setDividendProcessing: (state, action: PayloadAction<ApiDividendProcessing>) => {
      state.note.dividendProcessing = action.payload
    },
    setCounterpartyId: (state, action: PayloadAction<string>) => {
      state.note.counterPartyId = action.payload
      clearError('counterPartyId', state, state.note.counterPartyId)
    },
    clearErrors: state => {
      state.errors = {}
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    saveNoteFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const {
  init,
  setBusinessUnit,
  setClient,
  setCollateral,
  setCounterpartyId,
  setDividendProcessing,
  setMaturityDate,
  setMaturityDateType,
  setNoteName,
  setStartDate
} = newNoteSlice.actions

const { clearErrors, saveNoteFailure, setInvalid } = newNoteSlice.actions

const validate = validator(newNoteRules)

const moveNext = (note: ApiNote, client: ApiClient, businessUnit: ApiBusinessUnit) => dispatch => {
  dispatch(clearErrors())
  dispatch({ type: 'newNote/moveNext', payload: { note, client, businessUnit } })
}

const saveNoteAndMoveNext = () => async (dispatch, getState: () => { newNote: NewNoteState }) => {
  const state = getState().newNote
  const { note } = state

  const apiNewNote: AddNote = {
    noteName: note.noteName,
    startDate: getDateOnlyAsJson(note.startDate),
    endDate: getDateOnlyAsJson(note.endDate),
    dividendProcessing: note.dividendProcessing,
    businessUnitId: note.businessUnitId,
    isCollateralForLoan: note.isCollateralForLoan
  }

  try {
    const response = await api.tenant.noteCreate(TENANT_ID, apiNewNote)

    if (response.status === 200) {
      dispatch(moveNext(response.data, state.note.client, state.note.businessUnit))
    }
  } catch (e) {
    dispatch(saveNoteFailure(extractErrorMessagesFromServerResponse(e)))
  }
}

export const requestMoveNext = () => (dispatch, getState: () => { newNote: NewNoteState }) => {
  const state = getState().newNote

  const result = validate(state)

  if (result.isValid) {
    dispatch(saveNoteAndMoveNext())
  } else {
    dispatch(setInvalid(result.errors))
  }
}

export const actions = { ...newNoteSlice.actions, requestMoveNext }

function* fetchClientsSaga() {
  try {
    const response = yield call(api.usermanager.currentList)
    yield put({
      type: `newNote/fetchClientsSuccess`,
      payload: { clients: response.data.clients, userType: response.data.roles[0] }
    })
  } catch (error) {
    yield put({ type: `newNote/fetchClientsFailure`, payload: error })
  }
}

const fetchNotesSaga = createFetchSaga('newNote/fetchNotes', call(api.tenant.noteDetail, TENANT_ID))

const fetchCounterpartyIds = createFetchSaga(
  'newNote/fetchCounterpartyIds',
  call(api.tenant.counterpartyidsDetail, TENANT_ID)
)

function* fetchReferenceDataSaga() {
  yield all([fetchClientsSaga(), fetchNotesSaga(), fetchCounterpartyIds()])
}

export function* newNoteRootSaga() {
  yield all([takeEvery('newNote/init', fetchReferenceDataSaga)])
}

export default newNoteSlice.reducer
